import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CodeEditor from './iCode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Practice() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const test_id = searchParams.get("test_id");
  const [sessionData, setSessionData] = useState(null);
  const [hasEnded, setHasEnded] = useState(false);
  const [testInProgress, setTestInProgress] = useState(false);
  const sessionIdRef = useRef(generateSessionId());
  const codeEditorRef = useRef(null);

  // Generate a unique session ID for this test attempt
  function generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Check if we're returning from a refresh by looking for our session in sessionStorage
  useEffect(() => {
    const savedSession = sessionStorage.getItem('testSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setSessionData(session);
        
        // Check if the test was already ended
        if (session.hasEnded) {
          setHasEnded(true);
        }
      } catch (e) {
        console.error('Failed to parse saved session data');
      }
    } else {
      // This is a new session, save it to sessionStorage
      const newSession = {
        sessionId: sessionIdRef.current,
        startTime: new Date().toISOString(),
        testId: location.state?.testId,
        testInProgress: false,
        hasEnded: false
      };
      sessionStorage.setItem('testSession', JSON.stringify(newSession));
    }
    
    return () => {
      // Clean up by removing session data when component unmounts normally
      if (!hasEnded) {
        sessionStorage.removeItem('testSession');
      }
    };
  }, []);

  // Save current test state to sessionStorage whenever important state changes
  useEffect(() => {
    const currentSession = {
      sessionId: sessionIdRef.current,
      startTime: sessionData?.startTime || new Date().toISOString(),
      testId: location.state?.testId,
      testInProgress: testInProgress,
      hasEnded: hasEnded
    };
    sessionStorage.setItem('testSession', JSON.stringify(currentSession));
  }, [testInProgress, hasEnded, sessionData]);

  // Start the test
  const handleStart = () => {
    setTestInProgress(true);
  };

  // Submit the test
  const handleSubmitTest = async () => {
    if (hasEnded) return;
    setHasEnded(true);
    
    // Clear session data
    sessionStorage.removeItem('testSession');
    setTestInProgress(false);
    
    try {
      // Trigger the submission in the CodeEditor component if possible
      if (location.state?.testId) {
        navigator.sendBeacon(`/api/submit_code?test_id=${location.state.testId}`, { method: "POST" });
      }
    } catch (error) {
      console.error("Error submitting test:", error);
    }
  };

  return (
    <div className={cn("min-h-screen")}>
      {/* Render the Code Editor component */}
      {!hasEnded && (
        <CodeEditor 
          testId={location.state?.testId} 
          ref={codeEditorRef}
          onTestEnd={handleSubmitTest}
        />
      )}
      
      {/* Completion message when test ends */}
      <Dialog open={hasEnded} onOpenChange={() => {}}>
        <DialogContent className={cn("sm:max-w-[425px] text-center")}>
          <DialogHeader>
            <CheckCircle2 className={cn("w-16 h-16 text-green-500 mx-auto mb-4")} />
            <DialogTitle className={cn("text-2xl font-bold")}>Test Submitted</DialogTitle>
            <DialogDescription>
              Your test has been successfully submitted. Thank you for completing the test!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex justify-center")}>
            <Button onClick={() => navigate('/user-home')}>Return to Home</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}